import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  faCopy,
  faDownload,
  faFolderMinus,
  faFolderPlus,
  faPenToSquare,
  faShare,
  faThumbTack,
  faTrash,
  faTrashUndo,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuProps } from "antd/es/menu";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import Resource from "@mapmycustomers/shared/types/entity/Resource";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { Popconfirm } from "@mapmycustomers/ui";

import { getCurrentUserId } from "@app/store/iam";

import { isResourceOrFolderSharingAllowed } from "../../store";
import { TRASH_FOLDER } from "../../utils/const";

import styles from "./ActionsButton.module.scss";
import messages from "./messages";

export enum ResourceAction {
  COPY_LINK = "copyLink",
  DELETE = "delete",
  DOWNLOAD = "download",
  MOVE_TO_FOLDER = "moveToFolder",
  PIN = "pin",
  REMOVE_FROM_FOLDER = "removeFromFolder",
  RENAME = "rename",
  RESTORE = "restore",
  SHARE = "share",
  UNPIN = "unpin",
}

export const analyticsNames: Record<ResourceAction, string> = {
  [ResourceAction.COPY_LINK]: "Copy Link",
  [ResourceAction.DELETE]: "Delete",
  [ResourceAction.DOWNLOAD]: "Download",
  [ResourceAction.MOVE_TO_FOLDER]: "Add to Folder",
  [ResourceAction.PIN]: "Pin",
  [ResourceAction.REMOVE_FROM_FOLDER]: "Remove from Folder",
  [ResourceAction.RENAME]: "Rename",
  [ResourceAction.RESTORE]: "Restore",
  [ResourceAction.SHARE]: "Share",
  [ResourceAction.UNPIN]: "Unpin",
};

const useItems = (
  handleCopyLink: (info: MenuInfo) => void,
  handleDelete: () => void,
  handleDownload: () => void,
  folderId: string | undefined,
  resource: Resource,
  isPinned: boolean,
  showMoveToFolderModal: () => void,
  showRenameModal: () => void,
  showShareModal: () => void,
  handlePinUnpin: () => void,
  onRemoveFromFolder: () => void,
  onRestore: () => void
) => {
  const intl = useIntl();
  const { pathname } = useLocation();

  const currentUserId = useSelector(getCurrentUserId);
  const sharingAllowed = useSelector(isResourceOrFolderSharingAllowed);

  const isLink = isNotEmpty(resource.url);
  const hasFolder = isNotEmpty(resource.folder);
  const isTrash = pathname.includes(TRASH_FOLDER);
  const sharedResource = resource.userId !== currentUserId;

  const items: MenuProps["items"] = useMemo(
    () => [
      ...(isTrash
        ? [
            {
              icon: <FontAwesomeIcon icon={faTrashUndo} />,
              key: ResourceAction.RESTORE,
              label: intl.formatMessage(messages.restore),
              onClick: onRestore,
            },
          ]
        : [
            ...(sharedResource || !sharingAllowed
              ? []
              : [
                  {
                    icon: <FontAwesomeIcon icon={faShare} />,
                    key: ResourceAction.SHARE,
                    label: intl.formatMessage(messages.share),
                    onClick: showShareModal,
                  },
                ]),
            {
              icon: <FontAwesomeIcon icon={faThumbTack} />,
              key: isPinned ? ResourceAction.UNPIN : ResourceAction.PIN,
              label: intl.formatMessage(messages.pin, { isPinned }),
              onClick: handlePinUnpin,
            },
            ...(isLink
              ? []
              : [
                  {
                    icon: <FontAwesomeIcon icon={faDownload} />,
                    key: ResourceAction.DOWNLOAD,
                    label: intl.formatMessage(messages.download),
                    onClick: handleDownload,
                  },
                ]),
            {
              icon: <FontAwesomeIcon icon={faCopy} />,
              key: ResourceAction.COPY_LINK,
              label: intl.formatMessage(messages.copyLink),
              onClick: handleCopyLink,
            },
            ...(sharedResource
              ? []
              : [
                  {
                    icon: <FontAwesomeIcon icon={faPenToSquare} />,
                    key: ResourceAction.RENAME,
                    label: intl.formatMessage(isLink ? messages.editLink : messages.rename),
                    onClick: showRenameModal,
                  },
                  {
                    icon: <FontAwesomeIcon icon={faFolderPlus} />,
                    key: ResourceAction.MOVE_TO_FOLDER,
                    label: intl.formatMessage(messages.moveToFolder),
                    onClick: showMoveToFolderModal,
                  },
                  ...(hasFolder && isNotEmpty(folderId)
                    ? [
                        {
                          icon: <FontAwesomeIcon icon={faFolderMinus} />,
                          key: ResourceAction.REMOVE_FROM_FOLDER,
                          label: intl.formatMessage(messages.removeFromFolder),
                          onClick: onRemoveFromFolder,
                        },
                      ]
                    : []),
                ]),
          ]),
      ...(sharedResource
        ? []
        : [
            {
              key: ResourceAction.DELETE,
              label: (
                <Popconfirm
                  danger
                  description={intl.formatMessage(messages.deleteConfirm, { isTrash })}
                  okText={intl.formatMessage(messages.delete, { isTrash })}
                  onConfirm={handleDelete}
                  overlayClassName={styles.popconfirm}
                  placement="left"
                  trigger={["click"]}
                  zIndex={1050} // to show popconfirm above dropdown menu
                >
                  <div className={styles.delete} onClick={stopEvents}>
                    <FontAwesomeIcon className={styles.deleteIcon} icon={faTrash} />
                    {intl.formatMessage(messages.delete, { isTrash })}
                  </div>
                </Popconfirm>
              ),
            },
          ]),
    ],
    [
      folderId,
      handleCopyLink,
      handleDelete,
      handleDownload,
      handlePinUnpin,
      hasFolder,
      intl,
      isLink,
      isPinned,
      isTrash,
      onRemoveFromFolder,
      onRestore,
      sharedResource,
      sharingAllowed,
      showMoveToFolderModal,
      showRenameModal,
      showShareModal,
    ]
  );

  return items;
};

export default useItems;
